export type SystemMaintenance = {
  /** Approximate end set by ops; null when not given or unparseable. */
  endDateTime: Date | null;
};

const nonEmpty = (value: unknown): string | null =>
  typeof value === "string" && value.trim() ? value.trim() : null;

const parseDate = (value: unknown): Date | null => {
  const raw = nonEmpty(value);
  if (!raw) return null;
  const date = new Date(raw);
  return Number.isNaN(date.getTime()) ? null : date;
};

/**
 * Parses the `system_maintenance` flag value: either an ISO 8601 date-time
 * string, or a JSON object `{ endDateTime }`. The flag being enabled is what
 * puts the app into maintenance; the value only adds the approximate end, so
 * a missing or malformed value still blocks the app, just without a time.
 */
export const parseSystemMaintenance = (value: unknown): SystemMaintenance => {
  const raw = nonEmpty(value);
  if (!raw) return { endDateTime: null };

  let parsed: unknown = raw;
  try {
    parsed = JSON.parse(raw);
  } catch {
    // Plain string value: treat it as the end date-time.
  }

  if (typeof parsed === "object" && parsed !== null) {
    return {
      endDateTime: parseDate((parsed as Record<string, unknown>).endDateTime)
    };
  }
  return { endDateTime: parseDate(raw) };
};

export type MaintenanceEndFormatOptions = {
  /** Defaults to the browser locale. */
  locale?: string;
  /** Defaults to the browser time zone. */
  timeZone?: string;
};

const calendarDay = (
  date: Date,
  { locale, timeZone }: MaintenanceEndFormatOptions
) =>
  new Intl.DateTimeFormat(locale, {
    timeZone,
    year: "numeric",
    month: "numeric",
    day: "numeric"
  }).format(date);

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * The approximate end as a short phrase in the user's own locale and time
 * zone, e.g. "today at 11:00 BST", "tomorrow at 09:30 CET" or
 * "Monday, 5 October at 11:00 BST". Returns null once the end has passed, so
 * the caller can say something more honest than a time in the past.
 */
export const formatMaintenanceEnd = (
  end: Date,
  now: number = Date.now(),
  options: MaintenanceEndFormatOptions = {}
): string | null => {
  if (end.getTime() <= now) return null;

  const time = new Intl.DateTimeFormat(options.locale, {
    timeZone: options.timeZone,
    hour: "2-digit",
    minute: "2-digit",
    timeZoneName: "short"
  }).format(end);

  const endDay = calendarDay(end, options);
  if (endDay === calendarDay(new Date(now), options)) return `today at ${time}`;
  if (endDay === calendarDay(new Date(now + DAY_MS), options)) {
    return `tomorrow at ${time}`;
  }

  const day = new Intl.DateTimeFormat(options.locale, {
    timeZone: options.timeZone,
    weekday: "long",
    day: "numeric",
    month: "long"
  }).format(end);
  return `${day} at ${time}`;
};
